/**
 * THE SENSE RECIPE — one rail, five senses, and whatever a user asks for next.
 *
 * A recipe is the WHOLE description of a sense: how to list what is new since a
 * cursor, how to read one item, and which fields the resulting card may carry.
 * One executor runs them all, so every sense inherits one set of caps, one
 * cursor discipline, one metering path and one minimizer — the July lesson,
 * where five hand-wired senses each grew their own copy of each and each copy
 * grew its own bug.
 *
 * WHY THE CONTRACT LIVES IN SHARED. A shipped recipe is code in the server; a
 * user recipe is a row an agent wrote on the user's behalf. Both are the same
 * shape, so the shape — and the validator that a user recipe must survive —
 * belongs where neither owns it.
 *
 * `fields` IS THE ALLOWLIST, and it is the privacy invariant of the whole rail:
 * the minimizer walks it and nothing else, so a path a recipe did not declare is
 * never read off a provider payload. That is why `validateSenseRecipe` refuses
 * an item or card path that no field declares — by the time the executor runs,
 * an undeclared path is silently empty, which reads as "the provider had
 * nothing" rather than "this recipe asked for something it may not have".
 *
 * THE ENVELOPE IS NOT THE ITEM. `list.itemsPath`, `list.cursor.path` and
 * `push.payloadItemPath` walk the tool's RESPONSE, not one item, and they are
 * deliberately NOT checked against `fields`: they say where the items ARE, not
 * what a card keeps.
 *
 * A BODY PATH IS NOT ONE OF THEM (A-R20). `read.bodyPath` and `deeper.bodyPath`
 * are the two paths that carry CONTENT, so they are declared fields like any
 * other: the whole point of the allowlist is that a recipe names what it reads,
 * and the body is the largest thing it ever reads. The body still reaches only
 * the gist call and is dropped after it, never stored on a card.
 *
 * TWO GRAMMARS, AND THEY ARE NOT INTERCHANGEABLE. A `FieldPath` names a
 * DECLARED PATH (`internalDate`); a `Template` slot names a DECLARED LABEL
 * (`{{when}}`) or one of the `RAIL_SLOTS` the executor supplies. Mixing them is
 * the likely first mistake, so the validator says which one it found.
 *
 * ONE SLOT NAMESPACE, SO A SLOT HAS ONE MEANING. Labels and rail slots share
 * `{{…}}`, so a field labelled `id` would make `{{id}}` mean two things and the
 * winner would be whichever check ran first. The validator therefore REFUSES a
 * declared label equal to a rail slot — the namespace is split at the recipe,
 * not resolved at render time. What each rail slot means:
 *
 *   - `{{id}}` — the ITEM's id, read at `item.idPath`. Needs no backing.
 *   - `{{threadId}}` `{{channelId}}` `{{fileId}}` `{{eventId}}` (`REFS_SLOTS`) —
 *     the card's `refs` value under that same key, and NOTHING else fills them.
 *     So one of these in any template — an arg, the title, the link — is
 *     REFUSED unless `card.refs` declares that key. Without the check the slot
 *     is legal, renders to null at the beat, and the argument degrades in
 *     silence: a `deeper` call that opens the wrong file, or none.
 *   - `{{cursor}}` `{{cursorTs}}` `{{sinceIso}}` `{{sinceEpoch}}`
 *     `{{sinceDate}}` `{{sinceTs}}` `{{nowIso}}` `{{plus14dIso}}` `{{selfId}}` —
 *     the beat's, from the stored cursor, the clock and the user's identity on
 *     the app. They need no backing either.
 *
 *     `{{cursorTs}}` AND `{{sinceTs}}` ARE NOT THE SAME INSTANT, and a recipe
 *     that widens its window (`windowBackDays`) is where they part: the first is
 *     the CURSOR as a Slack stamp, the second is that list's own WINDOW START.
 *     An expansion that bounds itself on the cursor starves a container the beat
 *     did not reach; one that bounds itself on the window re-reads it and the
 *     store's own id drops what is already held (A-R92).
 *
 * So a field that holds the thread id is labelled something else (`thread`) and
 * reached as a path; `{{threadId}}` stays the ref.
 *
 * THE CAPS CLAMP; THEY NEVER REFUSE. A recipe asking for 500 items a beat is
 * not malformed, it is just optimistic — it gets 50. Refusing there would turn
 * a rail limit into a recipe error, and the rail's limits are ours to change.
 *
 * AN ID IS NOT A PERSON (A-R34). A push payload often names its author by an
 * opaque id and nothing else — a Slack DM carries `user`, no name and no
 * address — so a card built from the payload alone has a blank Who line, which
 * reads as "a message from nobody". A `resolve` closes that: one extra call per
 * item that asks the app who an id is, whose answer becomes a VIRTUAL declared
 * field of kind `person` under the resolve's own `label`. So `card.who` names
 * the label like any other field and it renders through the same person
 * renderer, while the raw id stays where it belongs, in `refs`.
 *
 * A RESOLVE READS THE ITEM AND NOTHING ELSE. Its `args` are templated from the
 * item's DECLARED field labels only — never a rail slot: the cursor, the clock
 * and the user's own id are the BEAT's facts, not this item's, and a resolve
 * that could not be filled would skip in silence. `path` is an envelope path
 * like `list.itemsPath`: it walks the resolve TOOL's answer, so no field
 * declares it.
 *
 * THEIR RELATION IS A DIFFERENT MATTER (A-R28). A recipe that lists more than it
 * can READ is malformed, because the surplus is carded with no body and the
 * cursor advances over it — so a recipe whose (clamped) item cap times its
 * per-item calls (the read, plus one per resolve) exceeds its (clamped) read
 * cap is refused, and the error says which numbers and why.
 */

/** A dot path into a provider payload; a numeric segment indexes an array. */
export type FieldPath = string;

/** A string whose `{{slot}}`s name a declared field LABEL or a rail slot. */
export type Template = string;

/** A JSON literal, or a `Template`, in a tool's argument position. */
export type ArgValue = string | number | boolean | null | ArgValue[] | { [k: string]: ArgValue };

/** What a declared field holds — the card renderer's only hint about a value. */
export type FieldKind = 'id' | 'time' | 'person' | 'text' | 'link' | 'number';

/** One entry in the allowlist: where the value is, what to call it, what it is. */
export interface FieldDecl {
  path: FieldPath;
  label: string;
  kind: FieldKind;
}

/**
 * One extra call that turns an id the item carries into a person (A-R34).
 *
 * `args` are templated from the item's own declared field LABELS; `path` walks
 * the answer of `tool`, not the item; the value found there becomes a virtual
 * declared field named `label`, which `card.who` and any template may then name.
 */
export interface SenseResolve {
  /** The virtual field's name — and its path, since it sits on the item itself. */
  label: string;
  tool: string;
  args: Record<string, ArgValue>;
  /** Where the person sits in the tool's ANSWER (an envelope path). */
  path: FieldPath;
  kind: 'person';
}

/**
 * HOW TO ASK A PROVIDER FOR THE NEXT PAGE, in the three shapes providers take.
 *
 * `from.row` — the value on the OLDEST row of the page just read becomes the
 * next page's bound. Slack's channel history is this: `latest` bounds a page
 * from above, and walking it down is how a lower-bound provider is drained.
 *
 * `from.answer` — a token the ANSWER carries, beside the rows rather than in
 * them. Twitter is this (`meta.next_token` into `next_token`); its `until_id`
 * exists but the live schema refuses it beside `start_time`, so a time-cursor
 * recipe there cannot page by a row's id at all (A-R103).
 *
 * `from.page` — a page NUMBER the answer states, with the count of pages beside
 * it. Slack's search is this and nothing else: its answer carries
 * `messages.paging.{page,pages}` and no cursor of any kind (captured live).
 * The walk asks for `page + 1` and stops when `page` reaches `pages`, so it
 * needs no `morePath` to know when it is done.
 *
 * `maxPages` bounds all three, and every page is a metered call the recipe's own
 * read cap must cover (A-R28).
 */
export type SenseDrain = {
  arg: string;
  from:
    | { row: FieldPath }
    | { answer: FieldPath }
    | { page: { at: FieldPath; of: FieldPath } };
  maxPages: number;
};

/** One reason a listed row never becomes a card. Every `path` is a declared one. */
export type SenseSkipRule =
  | { path: FieldPath; equalsSlot: 'selfId' }
  | { path: FieldPath; equals: string | number | boolean }
  | { path: FieldPath; present: true };

/**
 * EVERY SLOT THE RAIL SUPPLIES, and nothing else — a template slot that is not
 * one of these must name a declared field, or the recipe is refused.
 *
 * Cursor and time: `cursor` (the stored cursor verbatim), `cursorTs`,
 * `sinceIso` / `sinceEpoch` / `sinceDate` (the cursor as the provider wants to
 * read it), `nowIso`, `plus14dIso` (calendar's forward window).
 * Identity and per-item: `selfId` (the user's own id on the app), `id`,
 * `threadId`, `channelId`, `fileId`, `eventId`.
 * Paging: `pageSize` (the recipe's own `list.maxItemsPerBeat`), which a token
 * cursor MUST ask for — see the requirement below.
 */
export const RAIL_SLOTS = [
  'cursor',
  'sinceIso',
  'sinceEpoch',
  'sinceDate',
  'nowIso',
  'plus14dIso',
  'selfId',
  'id',
  'threadId',
  'channelId',
  'fileId',
  'eventId',
  'cursorTs',
  'sinceTs',
  'pageSize',
] as const;

export type RailSlot = (typeof RAIL_SLOTS)[number];

/**
 * The rail slots the CARD's `refs` fill, and the only ones that need something
 * behind them: a recipe using `{{fileId}}` must declare `card.refs.fileId`.
 * The executor reads them from the built refs by the same key.
 */
export const REFS_SLOTS = ['threadId', 'channelId', 'fileId', 'eventId'] as const;

/**
 * The rail's own ceilings. A recipe may ask for less; asking for more is
 * clamped, never refused (see the module note).
 */
export const SENSE_CAPS = {
  /** Items carded in one beat. */
  maxItemsPerBeat: 50,
  /** Per-item reads in one beat — each is a metered connector call. */
  maxReadsPerBeat: 25,
  /** Declared fields. A card is an account, not a copy of the payload. */
  maxFields: 40,
  /** Resolves per item (A-R34) — each is one more metered call per card. */
  maxResolvesPerItem: 2,
  /** Containers one beat may expand (A-R92) — each is a metered call. */
  maxExpandRowsPerBeat: 25,
  /**
   * Extra list calls one beat may make walking a lower-bound provider down
   * (A-R91). Three, because the point is to clear an ordinary busy window
   * inside one beat, not to page a whole history: past that the honest answer
   * is to say the channel is too busy and stop, which is what (c) does.
   */
  maxDrainPages: 3,
  /**
   * The furthest back a first beat may reach, in days — the rail's own month,
   * which is also what a recipe that names no window gets. A recipe may ask for
   * LESS (A-R50); no recipe may ask a connector for more history than the rail
   * would have read on its own.
   */
  maxFirstBeatLookbackDays: 30,
} as const;

/**
 * How a tool's `data` becomes JSON before the path walk. `json` is what every
 * tool returns and what every user recipe gets; a named parser is a shipped
 * thing, because it is code we wrote for one provider's markup.
 */
export type SenseParse = 'json' | 'granola-markup';

/** The whole description of a sense. */
export interface SenseRecipe {
  /** 'gmail-inbox', 'slack-mentions', 'linear-my-issues'. */
  name: string;
  /** Bumps on any field change; cards carry `name@version` in refs. */
  version: number;
  /** Composio toolkit slug — the evidence door's `app` segment. */
  app: string;
  /** Consent stamp (`nami_connector_consent.source`); shipped = the toolkit. */
  source: string;
  origin: 'shipped' | 'user';
  /** `poll` = the 6h beat; `push` = a Composio trigger slug. */
  mode: 'poll' | 'push';
  /** Both modes — how ONE item is identified. */
  item: {
    /**
     * The provider's item id → the cite id's tail.
     *
     * AN ID MAY TAKE MORE THAN ONE PATH (A-R78). A Slack message stamp is
     * unique inside its channel and nowhere else, so a search that spans
     * channels names its rows `<channel.id>_<ts>`: an array is joined with `_`,
     * every part is a declared path like a single one, and an item missing ANY
     * part has no id at all — a half-built id is two different messages wearing
     * one citation, which the store would silently collapse to one card.
     */
    idPath: FieldPath | FieldPath[];
    /**
     * A CONSTANT in front of the id, for a list whose rows do not carry the
     * container they came from (A-R78).
     *
     * `SLACK_FETCH_CONVERSATION_HISTORY` answers messages with `ts`, `user` and
     * `text` and NO channel — the channel was the argument — so a channel
     * recipe has no path to name and its ids would be bare stamps, colliding
     * with the next channel's and with the DM sense's. The recipe knows the
     * channel because it IS the recipe, so it says so here. It is not a path:
     * nothing is read off the payload, so the allowlist has no opinion on it.
     */
    idPrefix?: string;
    /** The item's own time — never the ingest time. */
    occurredAtPath: FieldPath;
    /**
     * An item that CHANGES rather than arrives (calendar's `updated`, drive's
     * `modifiedTime`): the card id becomes `<id>@<version>`, so an edit lands
     * as a new card instead of colliding with the old one on the store's key.
     */
    versionPath?: FieldPath;
  };
  /** Push only: which triggers, and where the item sits in their payload. */
  push?: {
    triggerSlugs: string[];
    payloadItemPath: FieldPath;
  };
  /** Poll only — "what is new since the cursor". */
  list?: {
    /**
     * `tool` (the default) calls `tool` with `args`. `picked-corpus` has no list
     * call at all: the executor iterates the files the user picked
     * (`source_meta.picked[]`) through the read tool, least-recently-checked
     * first — the drive fallback, for a connector scoped to `drive.file`.
     */
    kind?: 'tool' | 'picked-corpus';
    /** Required when `kind` is `tool`; a picked corpus has nothing to list. */
    tool?: string;
    args?: Record<string, ArgValue>;
    /** Where the items array lives in the response. */
    itemsPath?: FieldPath;
    /**
     * Where the next beat starts.
     *
     * A TOKEN CURSOR MAY NAME TWO PATHS, because a provider may name the token
     * two ways. Drive's changes feed answers `nextPageToken` while pages remain
     * and `newStartPageToken` ONLY on the last page — never both — so a cursor
     * that could read one of them would stall for ever on the other: on a quiet
     * Drive every page is the last one, on a busy one no page is. `path` is read
     * first, `elsePath` when it holds nothing.
     */
    cursor:
      | { kind: 'occurred-at' }
      | { kind: 'token'; path: FieldPath; elsePath?: FieldPath };
    /** A token cursor's FIRST fetch, before any cursor is stored. */
    startToken?: { tool: string; args: Record<string, ArgValue>; path: FieldPath };
    /**
     * A second call per listed row, where the list returns a container rather
     * than the item (slack's search hit → its thread).
     *
     * THE EXPANDED ROW IS READ ON TOP OF ITS PARENT, the same overlay the read
     * block already makes: a thread reply carries `ts`, `user` and `text` and
     * NOT the channel it is in, which the parent match does carry. So the item
     * a card is built from is `{ ...parent, ...row }` — the row wins every key
     * it has, and the parent supplies the context it dropped. A recipe must
     * therefore not declare a field the parent carries and the row does not
     * unless the parent's value is the one it wants (a search match's
     * `username` is the SEARCHER's, so no expanding recipe declares it).
     *
     * ONE CALL PER DISTINCT SET OF ARGUMENTS. Twenty of my own posts commonly
     * sit in eight threads, and eight fetches answer the same eight pages as
     * twenty would. The de-duplication key is the RENDERED arguments, so the
     * rail needs no idea of what a thread is.
     */
    expand?: {
      tool: string;
      args: Record<string, ArgValue>;
      itemsPath: FieldPath;
      /**
       * How many listed rows one beat may expand (A-R92) — its OWN number, not
       * `maxItemsPerBeat`.
       *
       * The two are different questions and reading one as the other loses
       * messages: `maxItemsPerBeat` caps the CARDS a beat leaves, while this
       * caps the CONTAINERS it opens. When the item cap fell below the number
       * of rows the list returns, a beat expanded only the first few and
       * dropped the rest with no counter and no alert — and the cursor still
       * advanced past the replies inside them. So a recipe that expands lists
       * exactly what it can expand, and says both numbers out loud.
       */
      maxRows: number;
    };
    /**
     * HOW TO ASK A LOWER-BOUND PROVIDER FOR THE NEXT PAGE DOWN (A-R91).
     *
     * Slack's channel history answers the NEWEST `limit` rows above `oldest`,
     * so a busy window arrives as a slice with the remainder BELOW it — which
     * moving the lower bound can never reach. `arg` is the argument that bounds
     * the page from ABOVE (`latest`), `fromPath` is the declared path whose
     * value on the OLDEST row of the page becomes that bound, and `maxPages` is
     * how many extra calls one beat may make to walk down.
     *
     * With the window fully listed, the cursor may advance to the newest CARDED
     * row without stepping over anything — which is what turns A-R86a's hold
     * from the rule into the last resort.
     */
    drain?: SenseDrain;
    /**
     * HOW COARSE THE LIST'S OWN WINDOW ARGUMENT IS, next to its cursor (A-R107).
     *
     * Slack's search takes `after:<date>` — a whole UTC day — while the cursor
     * this rail keeps is a message stamp. The two are not the same instant, and
     * the gap between them is where `slack-mentions` died: on a day with more
     * mentions than one page holds, the window's start did not move when the
     * cursor did, so every later beat listed the same oldest page, found every
     * row at or below the cursor, carded nothing, and paid for the list for ever.
     *
     * A list that says this may DRAIN even though it lists oldest-first, which
     * A-R93 half one otherwise refuses: that rule rests on "the remainder is
     * above the page and the next beat reaches it by advancing", and advancing
     * reaches nothing when the window's start is pinned to a day boundary the
     * cursor has not crossed.
     */
    windowGranularity?: 'day';
    /**
     * ONE value the rows do not carry as a field, read out of one they do
     * (A-R79).
     *
     * Slack's search answer has NO `thread_ts` key. A message that sits in a
     * thread carries its parent's stamp as a query parameter of its own
     * `permalink` and nowhere else — and the thread fetch will not take
     * anything else: asked for a REPLY's stamp it answers that one message
     * (captured live), which is the message the card already summarised. So
     * the parent key is read out of a DECLARED link field, and the value
     * becomes a virtual declared field of kind `id` under `label` — a resolve's
     * own idiom (A-R34) — which `card.refs`, a template and the expansion may
     * all then name, because all three need it.
     *
     * `elsePath` IS READ WHEN THE PARAMETER HOLDS NOTHING, the same two-path
     * shape a token cursor already uses: a message that is NOT in a thread has
     * no `thread_ts` anywhere, and its own stamp IS the thread's root. Without
     * it, six of every twenty mentions would carry no openable thread.
     *
     * It is attached to the rows the LIST returned, before any expansion, so an
     * expanded row inherits it from the parent it is read on top of.
     */
    derive?: { label: string; fromLink: FieldPath; param: string; elsePath?: FieldPath };
    /**
     * The rows this beat must not card, each a declared path and a test.
     *
     * `equalsSlot: 'selfId'` — the user's own (A-R38). My own reply in a thread
     * I follow and my own post in a channel I watch are not news to me, and a
     * card for either would be titled with my own name and distilled from my own
     * words. A recipe declaring this rule and unable to fill the slot FAILS THE
     * BEAT (A-R85): no card is better than the person's own words carded.
     *
     * `equals` — a literal the row must not carry. A mention that arrived inside
     * a DM is the DM sense's card, not a second one wearing a channel's clothes.
     *
     * `present` — a key the row must not have at all. A bot's post in a watched
     * channel carries `bot_id` and, live, no `subtype` at all, so presence is
     * the test that actually matches it; an author id no users.info will answer
     * for is a Who line that cannot be written.
     *
     * IT SITS ON THE LIST, not on the expansion, because both need the same
     * rules: it is applied to whatever rows the list yields, expanded or not, in
     * one place.
     */
    skipWhere?: SenseSkipRule[];
    /**
     * WHERE THE PROVIDER SAYS IT TRUNCATED (A-R86) — an envelope path, like
     * `itemsPath`, whose truthy value means rows remain below the page it sent.
     *
     * A NEWEST-FIRST LIST CANNOT BE DRAINED BY MOVING ITS LOWER BOUND. Slack's
     * channel history answers the NEWEST `limit` messages above `oldest`, so on
     * a burst the beat sees a slice and the rows below it are never in hand.
     * Advancing the cursor over ground the beat never listed loses those rows
     * for good and silently; so a truncated page HOLDS the cursor and says
     * `sense_page_overflow`, which is the bargain A-R23 already struck for a
     * token cursor. Without this path a recipe cannot say it was truncated, and
     * the rail will not guess: a provider that answers fewer rows than it could
     * have looks identical to one that answered everything.
     */
    morePath?: FieldPath;
    /**
     * How far BEFORE the cursor this list's own window starts, in days (A-R87).
     *
     * FOR A LIST THAT FINDS CONTAINERS RATHER THAN ITEMS. `slack-my-threads`
     * searches for MY posts and cards somebody else's REPLIES, so its cursor is
     * a reply's stamp while its list needs my post — which may be a week older.
     * With the window pinned to the cursor, a thread stopped being listed the
     * moment its first reply was carded, and every later reply in it was lost.
     *
     * It widens `{{sinceIso}}`, `{{sinceEpoch}}` and `{{sinceDate}}` and NOTHING
     * else: `{{cursorTs}}` stays the cursor, so the per-row freshness filter and
     * an expansion's own `oldest` are untouched and the wider list re-cards
     * nothing. Clamped to the rail's own month.
     */
    windowBackDays?: number;
    /** How the provider returns them, so the executor can walk oldest-first. */
    order: 'oldest-first' | 'newest-first';
    /** Hard cap, clamped to `SENSE_CAPS.maxItemsPerBeat`. */
    maxItemsPerBeat: number;
    /**
     * How far back the FIRST beat looks, in days, when nothing is stored and no
     * legacy cursor answers — the rail's own month unless a recipe says less
     * (A-R50). Clamped to `SENSE_CAPS.maxFirstBeatLookbackDays`.
     *
     * A PROVIDER MAY REFUSE THE MONTH. Google bounds `updatedMin`: the calendar
     * list answers HTTP 410 `updatedMinTooLongAgo` for a 30-day window, so that
     * sense never listed once on a fresh account. This is where a recipe says
     * what its own provider will actually serve.
     */
    firstBeatLookbackDays?: number;
    parse?: SenseParse;
  };
  /**
   * One item in full. The beat reads only when the card's gist needs a body;
   * the body reaches the gist call and is dropped, never stored.
   */
  read?: {
    tool: string;
    args: Record<string, ArgValue>;
    bodyPath?: FieldPath;
    /** Hard cap, clamped to `SENSE_CAPS.maxReadsPerBeat`. */
    maxReadsPerBeat: number;
    parse?: SenseParse;
  };
  /**
   * Per item, before the card is built: turn an id into a person (A-R34).
   * Each entry is ONE metered call, counted against `read.maxReadsPerBeat` on
   * the poll rail. Clamped to `SENSE_CAPS.maxResolvesPerItem`.
   */
  resolve?: SenseResolve[];
  card: {
    title: Template;
    /** 'email' | 'meeting' | 'event' | 'doc-change' | 'message' | free text. */
    what: string;
    /** People fields; rendered as name + domain, addresses stripped. */
    who: FieldPath[];
    when: FieldPath;
    /** `model` distils a gist; `fields` composes one from `fieldsForGist`. */
    gist: 'model' | 'fields';
    fieldsForGist?: FieldPath[];
    /** The app's own permalink, when the payload has one. */
    link?: Template;
    /** Pointers out: threadId, channelId, eventId, fileId … */
    refs: Record<string, FieldPath>;
  };
  /** THE ALLOWLIST. The minimizer walks this and nothing else. */
  fields: FieldDecl[];
  /**
   * The cursor the hand-wired sense kept before the rewrite (gmail's
   * `lastInternalDate`, granola's `lastMeetingAt`), read ONCE when the recipe's
   * own key is absent — so the rail never re-cards what the old poll had read.
   */
  legacyCursor?: { key: string; kind: 'epoch-ms' | 'iso' };
  /** What the dreamer's sense tool may open on one carded item (Train B). */
  deeper?: {
    tool: string;
    args: Record<string, ArgValue>;
    bodyPath: FieldPath;
    maxChars: number;
    /**
     * How the answer becomes JSON before the body path walks it — `read`'s own
     * field, for `read`'s own reason. Granola answers markup to every call it
     * serves, so a `deeper` on that tool without the parser walks a string and
     * finds nothing: an EMPTY answer wearing the face of a meeting with no notes.
     * Captured live in B.0 (`tests/fixtures/senses/granola-notes.json`).
     */
    parse?: SenseParse;
    /**
     * The noun for what lies behind the card — 'thread', 'notes' — named HERE
     * because only the recipe knows. It rides into the tool's result header
     * ("Opened ev:sense:gmail:… (gmail thread, 4,812 chars)"), so the model is
     * told what it is reading. A hand list in the belt keyed on `app` would be
     * the same fact kept somewhere it cannot be kept true; absent, the header
     * says "source", which is never wrong.
     */
    opens?: string;
  };
}

// =============================================================================
// The walker and the renderer — the two grammars, as functions
// =============================================================================

/**
 * `getPath`, `templateSlots`, `renderTemplate` and `senseFields` live in
 * `sense-recipe-walk.ts` (the same seam as the validator, A-R84: this file
 * crossed the size ratchet again, and what a recipe IS separates cleanly from
 * what you DO with one). Re-exported here so every importer, mock and test is
 * untouched.
 */
export { getPath, templateSlots, renderTemplate, senseFields } from './sense-recipe-walk.js';


// =============================================================================
// The reader
// =============================================================================

/**
 * IS THIS A RECIPE, and does it stay inside its own allowlist?
 *
 * It lives in `sense-recipe-validate.ts` (A-R84: this file crossed the size
 * ratchet and the seam was the obvious one — the TYPE says what a recipe IS,
 * the validator says whether a given one is one, and only the validator needs
 * zod). Re-exported here so every importer, mock and test is untouched.
 */
export { validateSenseRecipe } from './sense-recipe-validate.js';
